/** Shared private Assist carry/receipt shapes. Storage, preview authority and
 * original command lifetimes remain with their existing owners. */
import { z } from 'zod';
import { ApiError } from '@/lib/api';
import type { ApprovePrivateCoachingFeedbackRequest, PrivateCoachingFeedbackAcceptedResponse } from '@/lib/types';

export type PrivateFeedbackContent = ApprovePrivateCoachingFeedbackRequest['content'];
export type PrivateFeedbackRecording = {
  video_reference: NonNullable<ApprovePrivateCoachingFeedbackRequest['video_reference']>;
  recording_resource: NonNullable<ApprovePrivateCoachingFeedbackRequest['recording_resource']>;
};
export type PrivateFeedbackReviewCarry = {
  content: PrivateFeedbackContent;
  /** Locator only: the existing native Resource journal owns command/receipt. */
  recording_entry_id?: string;
  recording?: PrivateFeedbackRecording;
};
const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
const pin = z.object({ resource_id: uuid, revision_id: uuid }).strict();
const video = z.object({
  match_id: z.string(), match_slug: z.string(),
  slot: z.enum(['full', 'first_half', 'second_half', 'extra_time', 'penalty_shootout']),
  timestamp_seconds: z.number().finite().nonnegative(), reference_revision: z.string().regex(/^[a-f0-9]{64}$/),
}).strict();
const content = z.object({
  // Carry is editable writing, not submission admission. Never trim, clip or
  // reject over-limit strings here; explicit review validates actual limits.
  message: z.string(), next_step: z.string(),
  subject_mode: z.enum(['players', 'team']).nullable().optional(),
  player_ids: z.array(z.string()).optional(),
  audience: z.enum(['coaches', 'players', 'team']).nullable().optional(),
  associated_match_id: z.string().nullable().optional(),
  resource_links: z.array(pin).optional(),
  goal_ids: z.array(z.union([z.number().int().positive().max(Number.MAX_SAFE_INTEGER), z.string()])).optional(),
  ref_video_provider: z.enum(['youtube', 'vimeo', 'file']).nullable().optional(),
  ref_video_id: z.string().nullable().optional(), ref_video_label: z.string().nullable().optional(),
  ref_video_title: z.string().nullable().optional(), ref_video_thumb: z.string().nullable().optional(),
}).strict();
const carry = z.object({
  content,
  recording_entry_id: uuid.optional(),
  recording: z.object({ video_reference: video, recording_resource: pin }).strict().optional(),
}).strict().refine(value => !value.recording || value.content.resource_links?.some(link =>
  link.resource_id.toLowerCase() === value.recording!.recording_resource.resource_id.toLowerCase()
  && link.revision_id.toLowerCase() === value.recording!.recording_resource.revision_id.toLowerCase()));

/** A deep copy of closed typed data, with authored strings and ID
 * representation unchanged. The original review identity is validated by the
 * existing ReviewCarry owner, not inferred or reassigned by this decoder. */
export function decodePrivateFeedbackReviewCarry(value: unknown): PrivateFeedbackReviewCarry | null {
  const parsed = carry.safeParse(value);
  return parsed.success ? structuredClone(value as PrivateFeedbackReviewCarry) : null;
}

const receipt = z.object({
  schema_version: z.literal('coaching-private-feedback-accepted.v1'),
  artifact_kind: z.literal('feedback'), state: z.literal('private_draft_at_acceptance'),
  feedback_id: uuid, revision_id: uuid, version: z.number().int().min(1).max(2147483647),
}).strict();
/** A malformed success is uncertainty, never permission to create a new intent. */
export function decodePrivateFeedbackReceipt(value: unknown): PrivateCoachingFeedbackAcceptedResponse | null {
  const parsed = receipt.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export type PrivateApprovalFailure = {
  disposition: 'fresh_rejection' | 'retain_original';
  reason: 'validation' | 'source' | 'retry' | 'authority' | 'unavailable' | 'retired' | 'unknown';
  mayHaveAccepted: boolean;
};
const sourceRejections = new Set([
  'publication_preview_stale', 'confirmation_revision_conflict', 'revision_conflict', 'video_reference_stale',
]);
const retryCodes = new Set(['evidence_retry_required', 'transaction_retry_required', 'coherent_transaction_required']);
function requestValidation(detail: unknown): boolean {
  return Array.isArray(detail) && detail.length > 0 && detail.every(item =>
    item && typeof item === 'object' && Array.isArray(item.loc) && item.loc[0] === 'body'
    && typeof item.type === 'string' && typeof item.msg === 'string');
}
/** A current error cannot disprove an earlier uncertain acceptance. Only a
 * validated tagged receipt settles that history; GET/404 never does. */
export function classifyPrivateApprovalFailure(
  error: unknown, history: { mayHaveAccepted: boolean },
): PrivateApprovalFailure {
  const code = error instanceof ApiError && error.detail && typeof error.detail === 'object'
    && 'code' in error.detail && typeof error.detail.code === 'string' ? error.detail.code : null;
  const reason: PrivateApprovalFailure['reason'] = code && retryCodes.has(code) ? 'retry'
    : error instanceof ApiError && [401, 403].includes(error.status) ? 'authority'
    : code === 'operation_retired' ? 'retired'
    : error instanceof ApiError && error.status === 404 ? 'unavailable' : 'unknown';
  if (history.mayHaveAccepted) return { disposition: 'retain_original', reason, mayHaveAccepted: true };
  if (error instanceof ApiError) {
    if (error.status === 422 && requestValidation(error.detail)) {
      return { disposition: 'fresh_rejection', reason: 'validation', mayHaveAccepted: false };
    }
    if (error.status === 409 && code && sourceRejections.has(code)
        || error.status === 422 && code === 'video_resource_mismatch') {
      return { disposition: 'fresh_rejection', reason: 'source', mayHaveAccepted: false };
    }
    if (error.status === 409 && code && retryCodes.has(code)) {
      return { disposition: 'retain_original', reason: 'retry', mayHaveAccepted: false };
    }
  }
  return { disposition: 'retain_original', reason, mayHaveAccepted: true };
}
